import { useState, useContext } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performDeleteRequest } from '@atlassian/jira-fetch/src/utils/requests';
import { getStatusCodeGroup } from '@atlassian/jira-project-custom-roles/src/services/utils/index.tsx';
import type { Actor, AccessPageContext } from '../../common/types';
import { isUser } from '../../common/utils';
import MainProviderContext from '../access-page-context';
import { isValidationError } from '../utils';
import type { Props } from './types';

export const getRemoveActorFromProjectUrl = (
	projectContext: AccessPageContext,
	actor: Actor,
): string => {
	const { baseUrl, projectKey } = projectContext;
	const { type, roleTypeId } = actor;
	return `${baseUrl}/rest/projectconfig/latest/roles/${projectKey}/actor/?${
		isUser(type) ? 'user' : 'group'
	}=${encodeURIComponent(roleTypeId)}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeActorFromProject = async (url: string): Promise<any> =>
	performDeleteRequest(url);

const RemoveActorService = (props: Props) => {
	const { children, onActorRemovedSuccess, onActorRemovedFailure } = props;
	const projectContext = useContext(MainProviderContext);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<unknown>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onRemoveActor = async (actor?: Actor): Promise<any> => {
		if (actor) {
			setLoading(true);
			const url = getRemoveActorFromProjectUrl(projectContext, actor);
			try {
				await removeActorFromProject(url);
				setLoading(false);
				onActorRemovedSuccess(actor);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (httpError: any) {
				onActorRemovedFailure(isValidationError(httpError)); // if this is a validationError(400), then we assume it is a last admin error
				setLoading(false);
				setError(httpError.message);
				fireErrorAnalytics({
					meta: {
						id: 'getRemoveActorFromProject',
						packageName: 'jiraUsersAndRoles',
						teamName: 'olympus',
					},
					error: httpError,
					attributes: {
						roleTypeId: actor.roleTypeId,
						projectKey: projectContext.projectKey,
						statusCode: getStatusCodeGroup(httpError),
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		}
	};

	return children({ loading, error, onRemoveActor });
};

export default RemoveActorService;
